package com.eladfinish.bible.contest.data.remoteConfig

import com.eladfinish.bible.contest.data.startConfigListener
import com.eladfinish.bible.contest.network.call.CallResult
import com.eladfinish.bible.contest.utils.DeferredLock
import dev.gitlive.firebase.remoteconfig.FetchStatus
import dev.gitlive.firebase.remoteconfig.FirebaseRemoteConfig
import dev.gitlive.firebase.remoteconfig.FirebaseRemoteConfigValue
import dev.gitlive.firebase.remoteconfig.ValueSource
import io.github.aakira.napier.Napier
import kotlinx.coroutines.channels.BufferOverflow
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.map
import kotlin.time.Duration

interface FirebaseRemoteConfigDataSource {

    suspend fun startConfigListener()

    suspend fun fetchAndActivate(force: Boolean = false): Result<Boolean>

    suspend fun setMinimumFetchInterval(seconds: Duration)

    suspend fun setDefaults(defaults: Map<String, Any?>)

    fun getBoolean(key: String): Boolean?
    fun getDouble(key: String): Double?
    fun getInt(key: String): Int?
    fun getLong(key: String): Long?
    fun getString(key: String): String?

    fun getBooleanFlow(key: String): Flow<Boolean?>
    fun getDoubleFlow(key: String): Flow<Double?>
    fun getIntFlow(key: String): Flow<Int?>
    fun getLongFlow(key: String): Flow<Long?>
    fun getStringFlow(key: String): Flow<String?>
    fun getAll(): Map<String, FirebaseRemoteConfigValue>
}

class FirebaseRemoteConfigDataSourceImpl(
    private val firebaseRemoteConfig: FirebaseRemoteConfig?,
) : FirebaseRemoteConfigDataSource {

    private val fetchLock = DeferredLock<Result<Boolean>>()

    private val configChangedFlow = MutableSharedFlow<Unit>(
        replay = 1,
        onBufferOverflow = BufferOverflow.DROP_OLDEST,
    )

    init {
        // Store initial value in replay cache
        configChangedFlow.tryEmit(Unit)
    }

    override suspend fun startConfigListener() {
        firebaseRemoteConfig?.let { config: FirebaseRemoteConfig ->
            config
                .startConfigListener()
                .collect { result ->
                    when (result) {
                        is CallResult.Success -> {
                            Napier.d(tag = TAG) {
                                "Remote Config Update Success: ${result.data}"
                            }
                            processActivation(config.activate(), config.info.lastFetchStatus)
                        }

                        is CallResult.FeatureError -> Napier.e(tag = TAG) {
                            "Remote Config Update Error: ${result.featureError.message}"
                        }

                        else -> Unit
                    }
                }
        }
    }

    override suspend fun fetchAndActivate(force: Boolean): Result<Boolean> {
        return fetchLock.asyncWithLock(force = force) {
            firebaseRemoteConfig?.let { config: FirebaseRemoteConfig ->
                Napier.d(tag = TAG) {
                    "Fetching and activating Remote Config (force = $force)"
                }
                fetchAndActivate(config = config, force = force)
            } ?: Result.failure(Throwable("Firebase is not injected"))
        }
    }

    override suspend fun setMinimumFetchInterval(seconds: Duration) {
        firebaseRemoteConfig?.settings {
            Napier.d(tag = TAG) {
                "Updating Remote Config fetch interval (interval = $seconds)"
            }
            minimumFetchInterval = seconds
        }
    }

    /**
     * Asynchronously activates the most recently fetched configs, so that the fetched key value
     * pairs take effect.
     * @param config Config instance to fetch on
     * @param force Set to true to force fetch values from remote.
     * IMPORTANT: Force fetch should not be called too frequently to not get limited by Firebase API.
     * @return
     * True if the current call activated the fetched configs;
     *
     * If the fetched configs were already activated by a previous call, it instead returns a false.
     *
     * Null if error occurred during fetch or activation
     */
    private suspend fun fetchAndActivate(
        config: FirebaseRemoteConfig,
        force: Boolean = false,
    ): Result<Boolean> =
        runCatching {
            if (force) {
                config.fetch(minimumFetchInterval = null)
                config.activate()
            } else {
                config.fetchAndActivate()
            }
        }
            .onSuccess { activated -> processActivation(activated, config.info.lastFetchStatus) }
            .onFailure { thr ->
                Napier.e(
                    tag = TAG,
                    throwable = thr,
                    message = thr.message ?: thr.toString()
                )
            }

    private suspend fun processActivation(activated: Boolean, status: FetchStatus) {
        if (activated) configChangedFlow.emit(Unit).also {
            Napier.d(tag = TAG) { "Remote Config Activated with status=$status" }
        } else Napier.d(tag = TAG) {
            "Remote Config Already Activated with status=$status"
        }
    }

    override fun getBoolean(key: String): Boolean? =
        getRemoteConfigRawValue(key)?.asBoolean()

    override fun getDouble(key: String): Double? =
        getRemoteConfigRawValue(key)?.asDouble()

    override fun getInt(key: String): Int? =
        getRemoteConfigRawValue(key)?.asLong()?.toInt()

    override fun getLong(key: String): Long? =
        getRemoteConfigRawValue(key)?.asLong()

    override fun getString(key: String): String? =
        getRemoteConfigRawValue(key)?.asString()

    override fun getBooleanFlow(key: String): Flow<Boolean?> =
        getRemoteConfigValueFlow(
            key = key,
            get = FirebaseRemoteConfigDataSource::getBoolean,
            default = null,
        )

    override fun getDoubleFlow(key: String): Flow<Double?> =
        getRemoteConfigValueFlow(
            key = key,
            get = FirebaseRemoteConfigDataSource::getDouble,
            default = null,
        )

    override fun getIntFlow(key: String): Flow<Int?> =
        getRemoteConfigValueFlow(
            key = key,
            get = FirebaseRemoteConfigDataSource::getInt,
            default = null,
        )

    override fun getLongFlow(key: String): Flow<Long?> =
        getRemoteConfigValueFlow(
            key = key,
            get = FirebaseRemoteConfigDataSource::getLong,
            default = null,
        )

    override fun getStringFlow(key: String): Flow<String?> =
        getRemoteConfigValueFlow(
            key = key,
            get = FirebaseRemoteConfigDataSource::getString,
            default = null,
        )

    private fun getRemoteConfigRawValue(key: String): FirebaseRemoteConfigValue? =
        firebaseRemoteConfig?.getValue(key).takeIf { it?.getSource() != ValueSource.Static }

    private fun <T> getRemoteConfigValueFlow(
        key: String,
        get: FirebaseRemoteConfigDataSource.(key: String) -> T?,
        default: T,
    ): Flow<T> = configChangedFlow
        .map { this.get(key) ?: default }
        .distinctUntilChanged()

    override fun getAll(): Map<String, FirebaseRemoteConfigValue> {
        return firebaseRemoteConfig?.all.orEmpty()
    }

    override suspend fun setDefaults(defaults: Map<String, Any?>) {
        firebaseRemoteConfig?.setDefaults(*defaults.toList().toTypedArray())
    }

    private companion object {
        const val TAG = "FIREBASE_REMOTE_CONFIGS"
    }
}
